// CollectionsLayout — pure layout/context bridge for the Collections module.
//
// Pushes <CollectionsNav /> into the SidebarContext slot for the lifetime
// of the route subtree, then renders <Outlet /> for the actual page.
// Also renders <NarrowSidebar /> — fixed-position quick-access cubes
// pinned immediately to the right of the main sidebar.
//
// Why a layout component rather than wiring CollectionsNav directly into
// AppShell? It keeps each module self-contained — adding a future module
// is a matter of writing its own *Layout + *Nav, not editing AppShell or
// the router scaffolding. CollectionsNav unmounts cleanly when the user
// switches modules (e.g. navigates back to `/` HomePage), so the next
// module starts from a clean sidebar slot and the cubes go away.

import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useSidebarContent } from '../../components/shell/SidebarContext';
import { NarrowSidebar } from '../../components/shell/NarrowSidebar';
import { SubscriptionGate } from '../../components/SubscriptionGate/SubscriptionGate';
import { CollectionsNav } from './components/CollectionsNav';
import { SkipQuickActionsModal } from './components/SkipQuickActionsModal';
import { SkipNewEnquiryModal } from './components/SkipNewEnquiryModal';
import { SkipUpdateJobModal } from './components/SkipUpdateJobModal';
import { useCollectionsNotifications } from '../../hooks/useCollectionsNotifications';

const COLLECTIONS_NAV = <CollectionsNav />;

// Which Skip quick-action modal (if any) is currently open. Owned here so
// both the NarrowSidebar cube (a direct child) and the dashboard card
// (rendered via <Outlet />) can drive the same set of modals.
type SkipModalView = 'chooser' | 'enquiry' | 'job' | null;

// Openers handed to Collections pages through the router Outlet context.
// The dashboard's Skip card consumes these; other pages can ignore them.
export interface CollectionsOutletContext {
  openSkipEnquiry: () => void;
  openSkipUpdateJob: () => void;
}

// No-op fallback so pages that consume this hook still work when rendered
// outside CollectionsLayout (e.g. in isolated unit tests) — useOutletContext
// returns null without an enclosing <Outlet context=...>.
const NOOP_OUTLET_CONTEXT: CollectionsOutletContext = {
  openSkipEnquiry: () => undefined,
  openSkipUpdateJob: () => undefined,
};

// Plain React context for the Skip quick-action openers. React-Router's
// <Outlet context> does NOT propagate through an intermediate route that
// renders its own bare <Outlet /> (the RoleGate wrapper between this layout
// and the dashboard does exactly that), so the dashboard was falling back to
// the no-op openers. A plain context provider tunnels through any number of
// route/Outlet layers, so it reaches the dashboard regardless of nesting.
export const CollectionsActionsContext =
  createContext<CollectionsOutletContext | null>(null);

export function useCollectionsOutletContext(): CollectionsOutletContext {
  return useContext(CollectionsActionsContext) ?? NOOP_OUTLET_CONTEXT;
}

// Cube reserve — same documentElement.style.setProperty pattern Sidebar.tsx
// uses for `--sidebar-current-width`. CSS custom properties inherit DOWN
// the tree, so setting the variable on a CollectionsLayout-internal
// wrapper would not propagate to AppShell's <main> (an ancestor). Writing
// to documentElement lifts the variable to :root for as long as this
// layout is mounted, and the cleanup callback restores the global 0px
// default the moment the user navigates to a non-Collections module.
const CUBE_RESERVE_VALUE = 'calc(var(--sidebar-narrow-width) + 16px)';

export function CollectionsLayout() {
  // Stable JSX reference — keeps useSidebarContent's effect from firing
  // on every render of this layout.
  useSidebarContent(COLLECTIONS_NAV);

  // SSE notification feed — scoped to this layout so the stream only
  // connects while the user is in the Collections module.
  useCollectionsNotifications();

  const [skipModal, setSkipModal] = useState<SkipModalView>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(
      '--content-left-offset',
      CUBE_RESERVE_VALUE,
    );
    return () => {
      document.documentElement.style.removeProperty('--content-left-offset');
    };
  }, []);

  // Stable openers so the Outlet context identity doesn't churn each render.
  const outletContext = useMemo<CollectionsOutletContext>(
    () => ({
      openSkipEnquiry: () => setSkipModal('enquiry'),
      openSkipUpdateJob: () => setSkipModal('job'),
    }),
    [],
  );

  return (
    <CollectionsActionsContext.Provider value={outletContext}>
      <div data-print-hide>
        <NarrowSidebar onSkipQuickAction={() => setSkipModal('chooser')} />
      </div>
      {/* `context={outletContext}` kept for backward compatibility with any
          direct child route; the dashboard reads via CollectionsActionsContext
          because the intermediate RoleGate <Outlet /> would otherwise drop it. */}
      <Outlet context={outletContext} />

      {/* Skip quick-action modals — gated behind the Skip SubscriptionGate
          (the designated client module boundary). They render closed until
          a role-gated trigger (cube / dashboard card) opens them. */}
      <SubscriptionGate feature="skip">
        <SkipQuickActionsModal
          open={skipModal === 'chooser'}
          onClose={() => setSkipModal(null)}
          onNewEnquiry={() => setSkipModal('enquiry')}
          onUpdateJob={() => setSkipModal('job')}
        />
        <SkipNewEnquiryModal
          open={skipModal === 'enquiry'}
          onClose={() => setSkipModal(null)}
        />
        <SkipUpdateJobModal
          open={skipModal === 'job'}
          onClose={() => setSkipModal(null)}
        />
      </SubscriptionGate>
    </CollectionsActionsContext.Provider>
  );
}
